import axios, { AxiosError } from 'axios';
import { useAuthStore } from '@/store/authStore';

/**
 * Cliente axios centralizado para el panel web.
 *
 * En dev el proxy de Vite reenvía /api → http://localhost:3000.
 * En build de prod usa VITE_API_BASE_URL del .env.
 */
const baseURL = (import.meta.env.VITE_API_BASE_URL?.toString() ?? '') + '/api/v1';

export const apiClient = axios.create({
  baseURL: baseURL || '/api/v1',
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

/** Inyecta JWT Bearer en todas las requests. */
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Manejo global de errores.
 *  - 401 fuera de las entradas de sesión (/auth/login, /auth/google) → token
 *    vencido/inválido → logout y redirect al login.
 *  - 403 → sin permisos suficientes → el componente decide qué mostrar.
 *  - 5xx → se deja pasar al error boundary / toast del caller.
 */
apiClient.interceptors.response.use(
  (res) => res,
  (error: AxiosError) => {
    const status = error.response?.status;
    const url = error.config?.url ?? '';
    const isLoginCall = url.includes('/auth/login') || url.includes('/auth/google');

    if (status === 401 && !isLoginCall) {
      const { token, logout } = useAuthStore.getState();
      if (token) {
        console.warn('[apiClient] Token expirado, cerrando sesión.');
        logout();
        // Redirect duro — más simple que coordinarlo con React Router desde aquí.
        if (window.location.pathname !== '/login') {
          window.location.href = '/login?expired=1';
        }
      }
    }

    return Promise.reject(error);
  },
);

/** Extrae el mensaje de error más útil para mostrar al usuario. */
export function getApiErrorMessage(err: unknown, fallback = 'Ha ocurrido un error inesperado'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (Array.isArray(data?.message)) return data.message.join(', ');
    if (typeof data?.message === 'string') return data.message;
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
